import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MoreHorizontal from '@/icons/MoreHorizontal';

import './profile-more-menu.css';

export interface ProfileMoreMenuProps {
  canDelete: boolean;
  isDeleting?: boolean;
  showSuggest?: boolean;
  canManageAccess?: boolean;
  onDelete: () => void;
  onSuggest?: () => void;
  onPermissions: () => void;
  onManageAccess?: () => void;
}

const ProfileMoreMenu: React.FC<ProfileMoreMenuProps> = ({
  canDelete,
  isDeleting = false,
  showSuggest = false,
  canManageAccess = false,
  onDelete,
  onSuggest,
  onPermissions,
  onManageAccess
}) => {
  const { t } = useTranslation('forms');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="profile-more-menu" ref={ref}>
      <button
        type="button"
        className="btn btn--ghost btn--sm profile-more-menu__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('person.moreActions')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreHorizontal width={16} height={16} />
      </button>
      {open && (
        <div className="profile-more-menu__panel" role="menu">
          {showSuggest && (
            <button
              type="button"
              className="profile-more-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSuggest?.();
              }}
            >
              {t('person.suggestChanges')}
            </button>
          )}
          <button
            type="button"
            className="profile-more-menu__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onPermissions();
            }}
          >
            {t('person.whyPermissions')}
          </button>
          {canManageAccess && onManageAccess && (
            <button
              type="button"
              className="profile-more-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onManageAccess();
              }}
            >
              {t('person.manageAccess')}
            </button>
          )}
          {canDelete && (
            <>
              <div className="profile-more-menu__divider" aria-hidden />
              <button
                type="button"
                className="profile-more-menu__item profile-more-menu__item--danger"
                role="menuitem"
                disabled={isDeleting}
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
              >
                {isDeleting ? t('actions.deleting') : t('person.deletePerson')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileMoreMenu;
